import React, { useEffect } from 'react';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  MobileStepper,
  Paper,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { financialService } from '../utils/api';
import { buildSubmitPayload, calculateFundingSnapshot } from '../utils/fundingSnapshot';
import { BRANDING } from '../appConfig';

// Step components
import BirthYearForm from '../components/BirthYearForm';
import ExpensesInRetirementForm from '../components/ExpensesInRetirementForm';
import KidsPlanning from '../components/KidsPlanning';
import KidEducation from '../components/KidEducation';
import EmergencyFunds from '../components/EmergencyFunds';
import LifeGoalsForm from '../components/LifeGoalsForm';
import RiskProfileForm from '../components/RiskProfileForm';
// Net Worth card retired: net worth is now the total of the investment cards.
// import CurrentInvestmentForm from '../components/CurrentInvestmentForm';
import FundingStatusCard from '../components/FundingStatusCard';
// Replaced by one annual card (HouseholdFinances); kept for reference.
// import Income from '../components/Income';
// import Expense from '../components/Expense';
// import AnnualInvestments from '../components/AnnualInvestments';
import HouseholdFinances from '../components/HouseholdFinances';
import Debt from '../components/Debt';
import MutualFunds from '../components/MutualFunds';
import Equity from '../components/Equity';
import Global from '../components/Global';
import Others from '../components/Others';
// Fetch / manual choice card retired: fetch is the default, manual is a link on Connect Wealth.
// import InvestmentJourney from '../components/InvestmentJourney';
import WealthConnect from '../components/wealth/WealthConnect';
import FamilyMembers from '../components/FamilyMembers';
import FamilyWealth from '../components/wealth/FamilyWealth';
import { handleInnerBack } from '../components/wealth/backHandler';
import Employment from '../components/Employment';
import Experience from '../components/Experience';
// import AssetDistribution from '../components/AssetDistribution';
import LastPage from '../components/LastPage';

// Cashflow card: shown after the investment cards, just before About You.
const CASHFLOW_STEP = 'Your Cashflow';
// Last Goal Planning card; the Asset Allocation phase (and its step counter) starts after it.
const LAST_GOAL_STEP = 'Risk Profile';
// Shown after the investment cards (manual or fetched): the score needs their total.
const FUNDING_STEP = 'Funding Status';

const buildSteps = (state, actions) => [
  // ── Goal Planning ──────────────────────────────────────────── index 0-10
  { label: 'Birth Year',           component: <BirthYearForm /> },
  { label: 'Retirement Expenses',  component: <ExpensesInRetirementForm /> },
  { label: 'Kids',                 component: <KidsPlanning />, key: 'kids' },
  ...(state.kidsInfo?.length > 0
    ? [{ label: 'Kids Education', component: <KidEducation formData={state} onChange={actions.updateFormData} /> }]
    : []),
  { label: 'Emergency',            component: <EmergencyFunds /> },
  { label: 'Goals',                component: <LifeGoalsForm formData={state} onAddGoal={actions.addGoal} onRemoveGoal={actions.removeGoal} onChange={actions.updateFormData} /> },
  { label: 'Risk Profile',         component: <RiskProfileForm /> },
  // { label: 'Net Worth',            component: <CurrentInvestmentForm /> },
  // { label: 'Transition',           component: <TransitionCard /> },

  // ── Asset Allocation ───────────────────────────────────────── index 10+
  // NOTE: If the Kids Education conditional step is present the indices shift
  // by 1. GOAL_STEPS_COUNT must account for this — see note in AppContext.js.
  // { label: 'Income',               component: <Income /> },
  // { label: 'Expense',              component: <Expense /> },
  // { label: 'Annual Investments',   component: <AnnualInvestments /> },
  // { label: 'Investment Journey',   component: <InvestmentJourney /> },
  // Fetch for the user and family members, a collated view, then the manual cards pre-filled
  // with the fetched numbers so the user adds what could not be fetched (global, ESOP, ...).
  { label: 'Family Members',       component: <FamilyMembers /> },
  { label: 'Connect Wealth',       component: <WealthConnect /> },
  { label: 'Family Wealth',        component: <FamilyWealth /> },
  { label: 'Debt',                 component: <Debt /> },
  { label: 'Mutual Funds',         component: <MutualFunds /> },
  { label: 'Equity',               component: <Equity /> },
  { label: 'Global',               component: <Global /> },
  { label: 'Others',               component: <Others /> },
  { label: FUNDING_STEP,           component: <FundingStatusCard /> },
  { label: CASHFLOW_STEP,          component: <HouseholdFinances /> },
  { label: 'Employment',           component: <Employment /> },
  { label: 'Experience',           component: <Experience /> },
  // { label: 'Asset Distribution',   component: <AssetDistribution /> },
  { label: 'Last Page',            component: <LastPage /> },
];

// Steps that own their own navigation CTA — Next button is hidden/disabled
const SELF_NAVIGATING_STEPS = new Set([
  'Experience',
]);

const QuestionaryPage = () => {
  const theme = useTheme();
  const { state, actions } = useAppContext();

  const allSteps = buildSteps(state, actions);
  const totalSteps = allSteps.length;
  const activeStep = state.activeStep ?? 0;

  useEffect(() => {
    const currentStep = allSteps[activeStep];
    const stepLabel = currentStep?.label || 'Asset Allocation';
    document.title = `${stepLabel} | Asset Allocation - ${BRANDING.companyName}`;
  }, [activeStep, allSteps]);

  // Dynamic GOAL_STEPS_COUNT: the constant in AppContext assumes no Kids Education step.
  // Here we compute the real boundary at runtime so the display counter is always correct.
  const hasKidsStep = state.kidsInfo?.length > 0;
  const goalStepsCount = allSteps.findIndex(step => step.label === LAST_GOAL_STEP) + 1;
  const isGoalPhase = activeStep < goalStepsCount;

  // Display counter within the current phase
  const phaseStep  = isGoalPhase ? activeStep : activeStep - goalStepsCount;
  const phaseTotal = isGoalPhase ? goalStepsCount : totalSteps - goalStepsCount;
  const phaseLabel = isGoalPhase ? 'Goal Planning' : 'Asset Allocation';

  const currentStepDef = allSteps[activeStep];


  const isNextDisabled = () => {
    if (activeStep >= totalSteps - 1) return true;
    if (SELF_NAVIGATING_STEPS.has(currentStepDef?.label)) return true;
    if (currentStepDef?.label === 'Kids' && !state.kidsPlanningValid) return true;
    if (
      currentStepDef?.label === 'Birth Year' &&
      (!state.birthYear || state.age < 18 || state.age > 85)
    ) return true;
    if (currentStepDef?.label === 'Risk Profile' && !state.riskProfile) return true;
    // if (currentStepDef?.label === 'Net Worth' && !state.netWorth) return true;
    // if (currentStepDef?.label === 'Investment Journey' && !state.investmentJourney) return true;
    if (currentStepDef?.label === 'Family Members' && !state.familyMembersValid) return true;
    // Inside one person's connects: "Done" (or Back) first
    if (currentStepDef?.label === 'Connect Wealth' && state.wealthPersonOpen) return true;
    // Employment: city is mandatory but Next is NOT greyed out — we show inline error instead
    return false;
  };

  // Back first closes a screen open inside the current card (a person's connects, a holdings
  // list), then moves to the previous card.
  const handleBack = () => {
    if (handleInnerBack()) return;
    actions.prevStep();
  };

  const handleNext = () => {
    // Special handling for Employment: validate city before advancing
    if (currentStepDef?.label === 'Employment') {
      if (!state.city?.trim()) {
        // Signal Employment component to show the error
        actions.updateFormData('employmentNextAttempted', true);
        return; // do NOT advance
      }
    }
    // End of Goal Planning: save goals now so they are kept even if the user leaves
    // during the investment cards. Goals only: no Salesforce lead, net worth and score untouched.
    if (currentStepDef?.label === LAST_GOAL_STEP && state.phoneNumber) {
      const { amountRequiredToday } = calculateFundingSnapshot(state);
      financialService.submitRequest(buildSubmitPayload(state, amountRequiredToday, 0, 0, true))
        .catch((err) => console.error('Goal-planning save failed:', err));
    }
    actions.nextStep();
  };

  const renderCurrentStep = () => {
    if (!currentStepDef) {
      return (
        <Box p={2}>
          <Typography variant="h6" color="error">
            Step not found. Please try again.
          </Typography>
        </Box>
      );
    }
    return <Box>{currentStepDef.component}</Box>;
  };

  return (
    <Box sx={{ flexGrow: 1, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <a href="#main-content" style={{ position: 'absolute', top: '-40px', left: '0', backgroundColor: '#000', color: '#fff', padding: '8px', zIndex: 100 }} onFocus={(e) => e.target.style.top = '0'} onBlur={(e) => e.target.style.top = '-40px'}>
        Skip to main content
      </a>
      <AppBar position="static" sx={{ backgroundColor: '#000000' }}>
        <Toolbar>
          <a href={BRANDING.websiteUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={BRANDING.logoPath}
              alt={BRANDING.logoAlt}
              style={{ height: 20, marginRight: 30 }}
            />
          </a>
        </Toolbar>
      </AppBar>

      <main id="main-content" style={{ flexGrow: 1, overflowY: 'auto' }}>
        <Container
          sx={{
            py: 2,
            pb: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: { sm: '100%', md: '30%' },
          }}
        >
          <h1 className="sr-only">Asset Allocation Questionnaire ({phaseStep + 1} of {phaseTotal})</h1>
          <Box sx={{ p: 1 }}>{renderCurrentStep()}</Box>
        </Container>
      </main>

      <Paper
        square
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: { sm: '100%', md: '30%' },
          margin: { md: '0 auto' },
          zIndex: 100,
        }}
      >
        <MobileStepper
          variant="text"
          steps={phaseTotal}
          position="static"
          activeStep={phaseStep}
          sx={{
            maxWidth: '100%',
            bgcolor: '#000000',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
          }}
          nextButton={
            <IconButton
              size="small"
              onClick={handleNext}
              disabled={isNextDisabled()}
              sx={{
                color: isNextDisabled() ? 'grey' : 'white',
                '&.Mui-disabled': { color: 'grey' },
              }}
            >
              Next
              {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
          }
          backButton={
            <IconButton
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{
                color: activeStep === 0 ? 'grey' : 'white',
                '&.Mui-disabled': { color: 'grey' },
              }}
            >
              {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
              Back
            </IconButton>
          }
        />
      </Paper>
    </Box>
  );
};

export default QuestionaryPage;
